import { Food, FoodStatus } from '@cf-shared/data-model/food';
import { isRecipe } from '@cf-shared/data-model/recipe';

/**
 * How the recipe editor treats a food when it decides which share options, banner and
 * header icon to show (df-64bf2e6f1651).
 */
export enum RecipeShareRole {
  /** not a recipe, or a recipe the editor offers no sharing for */
  None = 'none',
  /** this user's own recipe: Share publishes it, Stop Sharing takes it back out */
  Own = 'own',
  /** a copy this user imported from somebody else's shared recipe: Share links to the original */
  Imported = 'imported',
  /** somebody else's shared recipe, opened from a link: Share links to it, Import copies it */
  Importable = 'importable',
}

/**
 * Which of those this recipe is.
 *
 * The author stamp (`local.hashSource`) and `local.shared` count as ownership first, as
 * they always did; an imported copy carries the stamp its duplication wrote. Both can go
 * away (the start-up sweep removes the stamp once the recipe is published, Stop Sharing
 * clears the flag), so a recipe stored as `FoodStatus.Private` that was not imported is
 * the user's own too. A recipe read from the global database never gets Private. See
 * `design-docs/sync-integrity.md`, "The app follows a fork wherever it publishes".
 */
export function recipeShareRole(food: Food): RecipeShareRole {
  // if it is not a recipe, the editor offers no recipe sharing at all
  if (!isRecipe(food)) {
    return RecipeShareRole.None;
  }
  // if it still carries the author stamp, or is shared, it is the user's own
  if (food.local?.hashSource || food.local?.shared) {
    return RecipeShareRole.Own;
  }
  // if it was imported, it is a copy of somebody else's recipe
  if (food.local?.sourceRecipeId) {
    return RecipeShareRole.Imported;
  }
  // only a user-made recipe can be shared. A legacy meal is stored as private too, but the
  // server refuses to publish one
  if (!food._id.startsWith('food/+UDR')) {
    return RecipeShareRole.None;
  }
  // if it is stored as private, it is the user's own even after both flags went away;
  // otherwise it is somebody else's shared recipe
  return food.status === FoodStatus.Private ? RecipeShareRole.Own : RecipeShareRole.Importable;
}
